import mapboxgl from "mapbox-gl";
import type { MapRef } from "react-map-gl";
import {
  StopBlock,
  GroupBlock,
  SegmentData,
  OperatorRecord,
  PlaceFragmentFragment,
} from "../types";

type Coordinates = [number, number];

export const getStopCoordinates = (
  stop: StopBlock,
  operatorByEntityId: Record<string, NonNullable<OperatorRecord>>,
): Coordinates | null => {
  const entityId = stop.entityRef?.entityId?.toString();
  const operator = entityId ? operatorByEntityId[entityId] : null;
  const coordinates = operator?.contact?.address?.location?.coordinates ?? [];
  if (coordinates.length < 2) return null;
  const [lng, lat] = coordinates;
  if (typeof lng !== "number" || typeof lat !== "number") return null;
  return [lng, lat];
};

/** Flatten all stop blocks from embedded segments. */
export const flattenStopBlocks = (segments: SegmentData[]): StopBlock[] => {
  const stops: StopBlock[] = [];
  for (const segment of segments) {
    for (const block of segment.items) {
      if (block.blockType === "STOP") {
        stops.push(block as StopBlock);
      } else if (block.blockType === "GROUP") {
        const group = block as GroupBlock;
        for (const child of group.items) {
          if (child.blockType === "STOP") {
            stops.push(child as StopBlock);
          }
        }
      }
    }
  }
  return stops;
};

export const getStopsWithCoordinates = (
  segments: SegmentData[],
  operatorByEntityId: Record<string, NonNullable<OperatorRecord>>,
) => {
  const stops = flattenStopBlocks(segments);
  return stops
    .map(stop => ({ item: stop, coordinates: getStopCoordinates(stop, operatorByEntityId) }))
    .filter(
      (
        row,
      ): row is {
        item: StopBlock;
        coordinates: Coordinates;
      } => Boolean(row.coordinates),
    );
};

export const buildBoundsFromCoordinates = (coordinates: Coordinates[]) => {
  if (coordinates.length === 0) return null;
  const bounds = new mapboxgl.LngLatBounds();
  coordinates.forEach(([lng, lat]) => {
    bounds.extend([lng, lat]);
  });
  return bounds;
};

export type FitMapBoundsOptions = {
  animated?: boolean;
  padding?: number;
  maxZoom?: number;
};

/** Convert LngLatBounds to the [[sw], [ne]] pair react-map-gl expects. */
export function getBoundsArray(
  bounds: mapboxgl.LngLatBounds,
): [[number, number], [number, number]] {
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();
  if (sw.lng === ne.lng && sw.lat === ne.lat) {
    const pad = 0.02;
    return [[sw.lng - pad, sw.lat - pad], [ne.lng + pad, ne.lat + pad]];
  }
  return [[sw.lng, sw.lat], [ne.lng, ne.lat]];
}

/** Fit the map to bounds once its container has non-zero dimensions. */
export function fitMapToBounds(
  mapRef: MapRef | null | undefined,
  bounds: mapboxgl.LngLatBounds | null | undefined,
  {
    animated = false,
    padding = 64,
    maxZoom = 15,
  }: FitMapBoundsOptions = {},
): boolean {
  if (!mapRef || !bounds) return false;
  const mapbox = mapRef.getMap?.();
  if (!mapbox?.isStyleLoaded?.()) return false;

  const container = mapbox.getContainer();
  if (!container || container.clientWidth === 0 || container.clientHeight === 0) {
    return false;
  }

  mapbox.resize();
  mapRef.fitBounds(getBoundsArray(bounds), {
    padding,
    duration: animated ? 2200 : 0,
    maxZoom,
    pitch: 0,
    bearing: 0,
    ...(animated ? { curve: 1.2 } : {}),
  });
  return true;
}

export const buildBoundsFromPlaceOutline = (place?: PlaceFragmentFragment | null) => {
  const geometry = place?.outline as
    | {
        coordinates?: number[][][][];
      }
    | undefined;

  const polygonCoordinates = geometry?.coordinates?.[0];
  if (!polygonCoordinates?.length) return null;

  const bounds = new mapboxgl.LngLatBounds();
  polygonCoordinates.forEach(([lng, lat]) => {
    if (typeof lng === "number" && typeof lat === "number") {
      bounds.extend([lng, lat]);
    }
  });
  return bounds;
};

/** Haversine distance between two [lng, lat] coordinate pairs, in kilometres. */
export const getKmDistanceBetweenCoordinates = (
  a: Coordinates,
  b: Coordinates,
): number => {
  const R = 6371;
  const dLat = ((b[1] - a[1]) * Math.PI) / 180;
  const dLng = ((b[0] - a[0]) * Math.PI) / 180;
  const lat1 = (a[1] * Math.PI) / 180;
  const lat2 = (b[1] * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(h));
};

export const getAutoZoomFromNeighborDistance = (
  coordinates: Coordinates,
  previousCoordinates?: Coordinates,
  nextCoordinates?: Coordinates,
) => {
  const distances: number[] = [];
  if (previousCoordinates) {
    distances.push(
      Math.hypot(
        coordinates[0] - previousCoordinates[0],
        coordinates[1] - previousCoordinates[1],
      ),
    );
  }
  if (nextCoordinates) {
    distances.push(
      Math.hypot(coordinates[0] - nextCoordinates[0], coordinates[1] - nextCoordinates[1]),
    );
  }
  if (!distances.length) return 13.5;

  const meanDistance = distances.reduce((sum, d) => sum + d, 0) / distances.length;
  if (meanDistance < 0.03) return 15.5;
  if (meanDistance < 0.08) return 14.5;
  if (meanDistance < 0.16) return 13.5;
  return 12;
};
